import React, { useEffect, useMemo, useState } from 'react';
import api from '../lib/api';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Search, Check } from 'lucide-react';
import { formatIDR } from '../lib/format';
import { Checkbox } from './ui/checkbox';

const CATS = ['Lighting', 'LED Screen', 'Rigging', 'Stage', 'Genset', 'Crew', 'Special FX', 'Multimedia', 'Other'];

export const ProductSelectorDialog = ({ open, onOpenChange, onPicked, multi = true }) => {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [category, setCategory] = useState('all');
  const [selected, setSelected] = useState({});

  useEffect(() => {
    if (!open) return;
    setSelected({});
    setSearch(''); setCategory('all');
  }, [open]);

  useEffect(() => {
    if (!open) return;
    setLoading(true);
    const params = { is_active: true };
    if (search) params.search = search;
    if (category !== 'all') params.category = category;
    api.get('/products', { params })
      .then(({ data }) => setProducts(data))
      .finally(() => setLoading(false));
  }, [open, search, category]);

  const toggle = (id) => setSelected(s => ({ ...s, [id]: !s[id] }));
  const selectedCount = Object.values(selected).filter(Boolean).length;

  const submit = () => {
    const picked = products.filter(p => selected[p.id]);
    if (picked.length === 0) return;
    onPicked(picked);
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[80vh] flex flex-col bg-card border-border" data-testid="product-selector-dialog">
        <DialogHeader>
          <DialogTitle>Pilih Produk / Layanan</DialogTitle>
          <DialogDescription>Pilih satu atau lebih produk aktif untuk ditambahkan.</DialogDescription>
        </DialogHeader>
        <div className="flex gap-2">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input className="pl-9" placeholder="Cari produk…" value={search} onChange={(e) => setSearch(e.target.value)} data-testid="product-selector-search" />
          </div>
          <Select value={category} onValueChange={setCategory}>
            <SelectTrigger className="w-[170px]"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Semua Kategori</SelectItem>
              {CATS.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div className="flex-1 overflow-y-auto rounded-lg border border-border">
          {loading && <div className="p-6 text-center text-muted-foreground">Memuat…</div>}
          {!loading && products.length === 0 && <div className="p-6 text-center text-muted-foreground">Tidak ada produk.</div>}
          <div className="divide-y divide-border">
            {products.map(p => (
              <label key={p.id} className="flex items-center gap-3 px-3 py-2.5 cursor-pointer hover:bg-white/4" data-testid={`product-selector-item-${p.id}`}>
                <Checkbox checked={!!selected[p.id]} onCheckedChange={() => toggle(p.id)} />
                <div className="flex-1 min-w-0">
                  <div className="font-medium text-sm">{p.product_name}</div>
                  <div className="text-xs text-muted-foreground truncate">{p.description}</div>
                  <div className="text-[10px] text-muted-foreground mt-0.5"><span className="px-1.5 py-0.5 rounded-full bg-white/5 border border-white/10">{p.category}</span> · {p.unit}</div>
                </div>
                <div className="text-right">
                  <div className="font-mono-num text-sm font-medium">{formatIDR(p.default_price)}</div>
                  <div className="text-xs text-muted-foreground">/ {p.unit}</div>
                </div>
              </label>
            ))}
          </div>
        </div>
        <DialogFooter>
          <div className="text-sm text-muted-foreground mr-auto">{selectedCount} dipilih</div>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>Batal</Button>
          <Button onClick={submit} disabled={selectedCount === 0} data-testid="product-selector-confirm"><Check className="h-4 w-4 mr-2" />Tambahkan</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
